import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LanguageClient, LanguageDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new LanguageClient(API_BASE_URL, axiosInstance);

/**
 * Hook for fetching cached language data (read-only)
 * Use this in forms/dropdowns that display language options
 */
export const useLanguages = () => {
    return useQuery({
        queryKey: ['languages'],
        queryFn: async () => {
            const response = await apiClient.searchLanguages({
                page: 1,
                pageSize: MAX_DROPDOWN_PAGE_SIZE,
                sortBy: 'DisplayPosition',
            });
            return response?.data?.results || [];
        },
        staleTime: 4 * 60 * 60 * 1000, // 4 hours
        refetchOnReconnect: true,
    });
};

/**
 * Hook for server-side paginated search (admin tables)
 * Direct API call with no caching - for real-time search results
 */
export const useLanguageSearch = () => {
    const search = async (params: {
        page: number;
        pageSize: number;
        sortBy?: string;
        sortDescending?: boolean;
        filters?: any;
    }) => {
        const response = await apiClient.searchLanguages({
            page: params.page,
            pageSize: params.pageSize,
            sortBy: params.sortBy || 'CreatedUtc',
            sortDescending: params.sortDescending ?? true,
            filters: params.filters || {},
        });
        return {
            results: response?.data?.results || [],
            totalCount: response?.data?.totalCount || 0,
        };
    };

    return { search };
};

/**
 * Hook for creating a new language
 * Usage: const createLanguage = useCreateLanguage();
 *        createLanguage.mutate(data);
 */
export const useCreateLanguage = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: LanguageDTO) => apiClient.createLanguage(data),
        onSuccess: () => {
            // Invalidate cache so all components refetch fresh data
            queryClient.invalidateQueries({ queryKey: ['languages'] });
        },
    });
};

/**
 * Hook for updating an existing language
 * Usage: const updateLanguage = useUpdateLanguage();
 *        updateLanguage.mutate({ id: '123', data: {...} });
 */
export const useUpdateLanguage = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: LanguageDTO }) => 
            apiClient.updateLanguage(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['languages'] });
        },
    });
};

/**
 * Hook for deleting a language
 * Usage: const deleteLanguage = useDeleteLanguage();
 *        deleteLanguage.mutate(id);
 */
export const useDeleteLanguage = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => apiClient.deleteLanguage(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['languages'] });
        },
    });
};

/**
 * Hook for fetching a single language by ID
 */
export const useLanguageById = (id: string) => {
    return useQuery({
        queryKey: ['languages', id],
        queryFn: async () => {
            const response = await apiClient.getLanguageById(id);
            return response?.data;
        },
        enabled: !!id, // Only fetch when ID is provided
    });
};